/*
 * Incoming-chat alerts, designed to be impossible to miss:
 *   - a bright four-note chime that repeats every 2.2 s until answered
 *   - persistent desktop notifications, re-raised every 20 s
 *   - a flashing tab title and a red badge on the tab icon
 *   - a badge on the installed app's dock/taskbar icon
 * The full-screen overlay lives in the Console view.
 *
 * Browsers only allow sound after the user has clicked something on the page,
 * so unlock() is called from the "Go available" click and plays a test ring.
 */

let ctx: AudioContext | null = null;
let ringTimer: number | null = null;
let titleTimer: number | null = null;
let notifyTimer: number | null = null;
let baseTitle = document.title;
let active = new Map<string, string>(); // conversation id → label
const shown = new Map<string, Notification>();

export function notificationsAllowed() {
  return 'Notification' in window && Notification.permission === 'granted';
}

export async function requestNotifications() {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  return (await Notification.requestPermission()) === 'granted';
}

/** Must run inside a click handler. Returns false if audio can't be unlocked. */
export async function unlock(test = true) {
  try {
    ctx ??= new AudioContext();
    if (ctx.state === 'suspended') await ctx.resume();
    if (test) burst();
    return ctx.state === 'running';
  } catch {
    return false;
  }
}

export function audioReady() {
  return Boolean(ctx && ctx.state === 'running');
}

/**
 * One soft bell tone: a sine fundamental plus two quiet overtones with a quick
 * attack and a natural exponential fade, so it sounds like a chime rather than a buzzer.
 */
function bell(freq: number, at: number, gain: number, decay = 1.4) {
  if (!ctx) return;
  const partials: [number, number][] = [[1, 1], [2, 0.28], [3, 0.08]];
  for (const [mult, level] of partials) {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq * mult;
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(gain * level, at + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, at + decay / mult);
    osc.connect(g).connect(ctx.destination);
    osc.start(at);
    osc.stop(at + decay + 0.05);
  }
}

/** One ring: a bright rising four-note chime (G5–B5–D6–G6), repeated every 2.2 s until answered. */
function burst() {
  if (!ctx) return;
  const now = ctx.currentTime + 0.02;
  [783.99, 987.77, 1174.66, 1567.98].forEach((f, i) => bell(f, now + i * 0.12, 0.34));
}

function setFavicon(count: number) {
  const link = (document.querySelector('link[rel="icon"]') as HTMLLinkElement) ?? Object.assign(document.head.appendChild(document.createElement('link')), { rel: 'icon' });
  if (!count) {
    link.href = '/icon-192.png';
    return;
  }
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const g = c.getContext('2d')!;
  g.fillStyle = '#21435a';
  g.beginPath();
  g.roundRect(0, 0, 64, 64, 14);
  g.fill();
  g.fillStyle = '#d93025';
  g.beginPath();
  g.arc(44, 20, 18, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = '#fff';
  g.font = 'bold 24px sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(String(Math.min(count, 9)), 44, 21);
  link.href = c.toDataURL('image/png');
}

function setAppBadge(count: number) {
  const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
  try {
    if (count) void nav.setAppBadge?.(count);
    else void nav.clearAppBadge?.();
  } catch {
    /* unsupported */
  }
}

function notify(id: string, label: string, onClick: (id: string) => void) {
  if (!notificationsAllowed()) return;
  shown.get(id)?.close();
  const n = new Notification('New chat — ' + label, {
    body: 'Click to answer. Other agents are being rung too.',
    tag: `chat-${id}`,
    requireInteraction: true, // stays on screen until clicked (Chrome/Edge)
    icon: '/icon-192.png',
    silent: false,
  });
  n.onclick = () => {
    window.focus();
    onClick(id);
    n.close();
  };
  shown.set(id, n);
}

/** Start or update alerts for the chats currently ringing this agent. */
export function ring(rings: { id: string; label: string }[], onClick: (id: string) => void) {
  const ids = new Set(rings.map((r) => r.id));
  for (const [id, n] of shown) {
    if (!ids.has(id)) {
      n.close();
      shown.delete(id);
    }
  }
  const newOnes = rings.filter((r) => !active.has(r.id));
  active = new Map(rings.map((r) => [r.id, r.label]));

  if (!rings.length) return stop();

  for (const r of newOnes) notify(r.id, r.label, onClick);
  setFavicon(rings.length);
  setAppBadge(rings.length);

  if (ringTimer === null) {
    burst();
    const loop = () => {
      burst();
      ringTimer = window.setTimeout(loop, 2200);
    };
    ringTimer = window.setTimeout(loop, 2200);
  }
  if (titleTimer === null) {
    baseTitle = document.title.startsWith('🔔') ? baseTitle : document.title;
    let on = false;
    titleTimer = window.setInterval(() => {
      on = !on;
      const first = [...active.values()][0] ?? '';
      document.title = on ? `🔔 NEW CHAT — ${first}` : `(${active.size}) ${baseTitle}`;
    }, 700);
  }
  if (notifyTimer === null) {
    // Re-raise notifications in case one was dismissed or scrolled away.
    notifyTimer = window.setInterval(() => active.forEach((label, id) => notify(id, label, onClick)), 20_000);
  }
}

export function stop() {
  if (ringTimer !== null) clearTimeout(ringTimer);
  if (titleTimer !== null) clearInterval(titleTimer);
  if (notifyTimer !== null) clearInterval(notifyTimer);
  ringTimer = titleTimer = notifyTimer = null;
  active.clear();
  shown.forEach((n) => n.close());
  shown.clear();
  if (document.title.startsWith('🔔') || document.title.startsWith('(')) document.title = baseTitle;
  setFavicon(0);
  setAppBadge(0);
}

/** A soft single chime for new messages in an accepted chat (not a ring). */
export function blip() {
  if (!ctx || ctx.state !== 'running') return;
  bell(880, ctx.currentTime + 0.01, 0.12, 0.9);
}

/** "Ding-dong" for a visitor waiting on a reply. */
function dingDong() {
  if (!ctx || ctx.state !== 'running') return;
  const t = ctx.currentTime + 0.01;
  bell(987.77, t, 0.22, 1.2);
  bell(783.99, t + 0.32, 0.22, 1.6);
}

/* -------------------------------------------------- "Visitor waiting" -- */

const nudged = new Map<string, Notification>();

/** The visitor has been waiting on a reply: chime, notify, and flash the title until it's answered. */
export function nudge(id: string, name: string, waitingSeconds: number, onClick: (id: string) => void) {
  dingDong();
  if (notificationsAllowed()) {
    nudged.get(id)?.close();
    const mins = Math.floor(waitingSeconds / 60);
    const n = new Notification(`${name} is waiting for your reply`, {
      body: `Waiting ${mins ? `${mins}m ` : ''}${waitingSeconds % 60}s. Click to answer.`,
      tag: `nudge-${id}`,
      requireInteraction: true,
      icon: '/icon-192.png',
    });
    n.onclick = () => {
      window.focus();
      onClick(id);
      n.close();
    };
    nudged.set(id, n);
  }
  if (titleTimer === null && !document.title.startsWith('⏳')) {
    const original = document.title;
    let on = false;
    const t = window.setInterval(() => {
      if (!nudged.size) {
        clearInterval(t);
        document.title = original;
        return;
      }
      on = !on;
      document.title = on ? `⏳ ${name} is waiting` : original;
    }, 900);
  }
  if (!notificationsAllowed()) nudged.set(id, { close() {} } as Notification);
}

export function clearNudge(id: string) {
  nudged.get(id)?.close();
  nudged.delete(id);
}
